import React from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Avatar,
  Box,
  Tooltip,
  Chip,
  Divider,
} from "@mui/material";
import { Favorite, FavoriteBorder, Share, LocationOn, CalendarToday, Group, Person } from "@mui/icons-material";
import { motion } from "framer-motion";
import { format, formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { toggleSavePost } from "../../redux/post/postSlice";
import { useDispatch } from "react-redux";
import { useNotification } from "../shared/NotificationProvider";

const PostCard = ({ post, isSaved }) => {
  const dispatch = useDispatch();
  const notify = useNotification();

  const formattedPrice = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  })
    .format(post.rent || 0)
    .replace("₹", "₹ ");

  const formattedAvailableDate = post.availableFrom
    ? format(new Date(post.availableFrom), "dd MMM yyyy")
    : null;

  const handleSavePost = async (postId) => {
    try {
      const res = await dispatch(toggleSavePost(postId));
      if (res.payload?.status === 'success') {
        notify.success(res.payload.message);
      } else {
        notify.error(res.payload?.message || "Failed to update save status");
      }
    } catch (err) {
      notify.error("System error occurred!");
    }
  };

  const handleCopyClick = async (id) => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/fmr/post-details/${id || ""}`
      );
      notify.success("Url copied to clipboard!");
    } catch {
      notify.error("Unable to copy to clipboard");
    }
  };

  const handleSharePost = (postId) => {
    handleCopyClick(postId);
  };

  return (
    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
      <Card
        sx={{
          borderRadius: "4px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          border: "1px solid #e0e0e0",
          overflow: "hidden",
          transition: "all 0.3s ease",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Header (Moved Author & Actions Here) */}
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Author Info */}
          <Box display="flex" alignItems="center">
            <Avatar src={post.author?.avatar} sx={{ width: 32, height: 32, mr: 1, bgcolor: "#001d3d" }}>
              {post.author?.name?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="caption" fontWeight={500} sx={{ color: "#212121" }}>
                {post.author?.name || "Anonymous"}
              </Typography>
              <Typography variant="caption" display="block" sx={{ color: "#777" }}>
                {formatDistanceToNow(new Date(post.updatedAt), { addSuffix: true })}
              </Typography>
            </Box>
          </Box>

          {/* Save & Share Actions */}
          <Box>
            <Tooltip title={isSaved ? "Unsave" : "Save"}>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleSavePost(post._id);
                }}
                sx={{
                  color: isSaved ? "#e53935" : "#555",
                  "&:hover": { backgroundColor: "transparent" },
                  "&:focus": { backgroundColor: "transparent" },
                  "&.Mui-focusVisible": { backgroundColor: "transparent" },
                }}
              >
                {isSaved ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Share">
              <IconButton
                size="small"
                onClick={(event) => { event.stopPropagation(); handleSharePost(post._id); }}
                sx={{
                  color: "#555",
                  "&:hover": { backgroundColor: "transparent" },
                  "&:focus": { backgroundColor: "transparent" },
                  "&.Mui-focusVisible": { backgroundColor: "transparent" },
                }}
              >
                <Share fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Link to={`/fmr/post-details/${post._id}`} style={{ textDecoration: "none", color: "inherit" }}>
          {/* Image with Price and Availability Badge */}
          <Box sx={{ position: "relative" }}>
            <CardMedia
              component="img"
              height={180}
              image={post.postImages[0]?.url || defaultImageSvg}
              alt={post.title}
              sx={{
                objectFit: "cover",
                filter: post.isAvailable === false ? "grayscale(80%)" : "none",
                opacity: post.isAvailable === false ? 0.8 : 1,
              }}
            />
            <Chip
              label={formattedPrice}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                backgroundColor: "#001d3d",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.8rem",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            />
          </Box>
        </Link>

        <CardContent sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
          {/* Title */}
          <Typography variant="subtitle1" fontWeight={600} sx={{
            mb: 0.5,
            color: "#212121",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
          }}>
            {post.title}
          </Typography>

          {/* Location */}
          <Box display="flex" alignItems="center" mb={1}>
            <LocationOn fontSize="small" sx={{ color: "#555", mr: 0.5 }} />
            <Typography variant="body2" sx={{ color: "#555" }}>
              {post.location?.split(",")[0]}
            </Typography>
          </Box>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              color: "#555",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              flexGrow: 0,
              mb: 1,
            }}
          >
            {post.description}
          </Typography>

          <Divider sx={{ my: 1 }} />

          {/* Available From */}
          {formattedAvailableDate && (
            <Box display="flex" alignItems="center" mb={0.5}>
              <CalendarToday fontSize="small" sx={{ color: "#555", mr: 0.5 }} />
              <Typography variant="body2" sx={{ color: "#555" }}>
                Available from {formattedAvailableDate}
              </Typography>
            </Box>
          )}

          {/* Roommates */}
          <Box display="flex" alignItems="center" mb={0.5}>
            <Group fontSize="small" sx={{ color: "#555", mr: 0.5 }} />
            <Typography variant="body2" sx={{ color: "#555" }}>
              {post.currentRoommates} current · {post.neededRoommates} needed
            </Typography>
          </Box>

          {/* Gender Preference */}
          {post.roommateShouldHaveGender && (
            <Box display="flex" alignItems="center" mb={0.5}>
              <Person fontSize="small" sx={{ color: "#555", mr: 0.5 }} />
              <Typography variant="body2" sx={{ color: "#555", textTransform: "capitalize" }}>
                {post.roommateShouldHaveGender} preferred
              </Typography>
            </Box>
          )}

          {/* Status */}
          {post.status && (
            <Box mt={1}>
              <Chip
                label={post.status}
                size="small"
                sx={{
                  backgroundColor: "#1a1a2e",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PostCard;

const defaultImageSvg = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="180" viewBox="0 0 300 180" fill="%23f0f0f0">
  <rect width="300" height="180" fill="%23e0e0e0"/>
  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="16" fill="%23999999">No Image Available</text>
</svg>`;
